import { motion } from "framer-motion";
import { AlertTriangle, Film, Gamepad2, Music, Wifi } from "lucide-react";
import { useTranslation } from "react-i18next";
import HandDrawnUnderline from "@/components/ui/hand-drawn-underline";

function PortalUnavailablePage() {
	const { t } = useTranslation();

	const services = [
		{
			id: "filmy",
			icon: Film,
			labelKey: "portal.services.movies",
			color: "from-red-500 to-pink-500",
			bgColor: "bg-red-500/10",
		},
		{
			id: "hudba",
			icon: Music,
			labelKey: "portal.services.music",
			color: "from-purple-500 to-indigo-500",
			bgColor: "bg-purple-500/10",
		},
		{
			id: "hry",
			icon: Gamepad2,
			labelKey: "portal.services.games",
			color: "from-green-500 to-emerald-500",
			bgColor: "bg-green-500/10",
		},
	];

	const containerVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				staggerChildren: 0.1,
				delayChildren: 0.3,
			},
		},
	};

	const itemVariants = {
		hidden: { y: 20, opacity: 0 },
		visible: {
			y: 0,
			opacity: 1,
			transition: {
				type: "spring",
				stiffness: 100,
			},
		},
	};

	const floatingVariants = {
		floating: {
			y: [0, -5, 0],
			transition: {
				duration: 3,
				repeat: 3,
				ease: "easeInOut",
			},
		},
	};

	return (
		<div className="relative min-h-screen overflow-hidden">
			{/* Static Background Elements */}
			<div className="-z-10 absolute inset-0">
				<div className="absolute top-20 left-10 h-32 w-32 rounded-full bg-gradient-to-r from-yellow-400/10 to-orange-400/10 blur-xl" />
				<div className="absolute top-40 right-20 h-24 w-24 rounded-full bg-gradient-to-r from-blue-400/10 to-purple-400/10 blur-xl" />
				<div className="absolute bottom-40 left-20 h-40 w-40 rounded-full bg-gradient-to-r from-green-400/10 to-cyan-400/10 blur-xl" />
			</div>

			<div className="container relative z-10 py-8 lg:py-16">
				{/* Main Content */}
				<motion.div
					initial={{ opacity: 0, scale: 0.9 }}
					animate={{ opacity: 1, scale: 1 }}
					transition={{ duration: 0.8, delay: 0.2 }}
					className="mx-auto max-w-6xl"
				>
					{/* Main Text */}
					<motion.div
						initial={{ opacity: 0, y: 30 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8, delay: 0.4 }}
						className="mb-16 text-center"
					>
						<h1 className="gradient-text-main mb-6 font-bold text-3xl leading-tight lg:text-5xl">
							{t("portal.main.title")}
							<br />
							{t("portal.main.subtitle")}
						</h1>
						<motion.div
							initial={{ scale: 0.8, opacity: 0 }}
							animate={{ scale: 1, opacity: 1 }}
							transition={{ duration: 0.6, delay: 0.6 }}
							className="relative inline-block"
						>
							<span className="bg-gradient-to-r from-yellow-400 to-yellow-500 bg-clip-text font-black text-4xl text-transparent lg:text-6xl">
								{t("portal.main.portal")}
							</span>
							<HandDrawnUnderline />
						</motion.div>
						<motion.p
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ delay: 0.8, duration: 0.8 }}
							className="mt-6 font-light text-muted-foreground text-xl lg:text-2xl"
						>
							{t("portal.main.description")}
						</motion.p>
					</motion.div>

					{/* Animated Services Grid */}
					<motion.div
						variants={containerVariants}
						initial="hidden"
						animate="visible"
						className="mb-16 flex flex-wrap justify-center gap-8"
					>
						{services.map((service) => {
							const IconComponent = service.icon;
							return (
								<motion.div
									key={service.id}
									variants={itemVariants}
									whileHover={{
										scale: 1.01,
										y: -1,
										transition: { duration: 0.2, ease: "easeOut" },
									}}
									className="group"
								>
									<div
										className={`relative flex w-56 flex-col items-center p-10 ${service.bgColor} rounded-2xl border border-white/20 backdrop-blur-sm transition-all duration-300`}
									>
										{/* Gradient overlay */}
										<div
											className={`absolute inset-0 bg-gradient-to-br ${service.color} rounded-2xl opacity-0 transition-opacity duration-300 group-hover:opacity-5`}
										/>

										<motion.div
											whileHover={{ scale: 1.03 }}
											transition={{ duration: 0.2 }}
											className={`mb-6 flex h-20 w-20 items-center justify-center bg-gradient-to-br ${service.color} rounded-2xl shadow-lg`}
										>
											<IconComponent className="h-10 w-10 text-white" />
										</motion.div>
										<span className="text-center font-semibold text-lg transition-colors">
											{t(service.labelKey)}
										</span>
									</div>
								</motion.div>
							);
						})}
					</motion.div>

					{/* Enhanced Warning Message */}
					<motion.div
						initial={{ opacity: 0, y: 30 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8, delay: 1 }}
						className="relative mb-8 overflow-hidden rounded-2xl border border-orange-200/30 bg-gradient-to-r from-orange-500/10 via-red-500/10 to-orange-500/10 p-8 backdrop-blur-sm dark:border-orange-800/30"
					>
						<div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-orange-500/5 to-red-500/5" />
						<div className="relative z-10 flex items-start gap-4">
							<motion.div
								initial={{ scale: 0 }}
								animate={{ scale: 1 }}
								transition={{ duration: 0.5, delay: 1.2 }}
							>
								<AlertTriangle className="mt-1 h-8 w-8 flex-shrink-0 text-orange-500" />
							</motion.div>
							<div>
								<p className="mb-2 font-semibold text-lg text-orange-600 dark:text-orange-400">
									{t("portal.warning.title")}
								</p>
								<p className="text-base text-foreground/80 leading-relaxed">
									{t("portal.warning.text")}
								</p>
							</div>
						</div>
					</motion.div>

					{/* Enhanced Footer */}
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ duration: 0.8, delay: 1.2 }}
						className="text-center"
					>
						<p className="text-lg text-muted-foreground">
							{t("portal.footer.text")}{" "}
							<motion.a
								href={t("portal.footer.link.url")}
								target="_blank"
								rel="noopener noreferrer"
								whileHover={{ scale: 1.01 }}
								className="font-semibold text-primary underline underline-offset-4 transition-colors hover:text-primary/80"
							>
								{t("portal.footer.link")}
							</motion.a>
						</p>
					</motion.div>
				</motion.div>

				{/* Thank you message moved to bottom */}
				<motion.div
					initial={{ opacity: 0, y: 30 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.8, delay: 1.4 }}
					className="mt-16 text-center"
				>
					<motion.div animate={floatingVariants.floating} className="mb-4 inline-block">
						<Wifi className="mx-auto h-12 w-12 text-yellow-500" />
					</motion.div>
					<motion.p
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 1.6, duration: 0.8 }}
						className="font-light text-lg text-muted-foreground"
					>
						{t("portal.thanks")}
					</motion.p>
				</motion.div>
			</div>
		</div>
	);
}

export default PortalUnavailablePage;
